import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';

// ionic native
import { Keyboard, KeyboardResizeMode } from '@awesome-cordova-plugins/keyboard/ngx';

// other libraries
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";

// rxjs
import { Subject } from "rxjs";
import { take, takeUntil, debounceTime } from "rxjs/operators";

// components
import { AutoCompleteComponent } from "../../components/auto-complete/auto-complete.component";

// services
import { AppointmentService } from "../../services/appointment.service";
import { ConversationService } from "../../services/conversation.service";
import { ParticipantService } from "../../services/participant.service";
import { PlatformService } from '../../services/platform.service';
import { OverlayService } from "../../services/overlay.service";
import { UserService } from "../../services/user.service";

// models
import { Appointment } from "../../models/appointment";
import { Event } from "../../models/event";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";


@Component({
    selector: 'appointment-add-participant',
    templateUrl: './appointment-add-participant.component.html',
    styleUrls: ['./appointment-add-participant.component.scss'],
})
export class AppointmentAddParticipantComponent implements OnInit, OnDestroy {

    /**
     * appointment
     *
     * @type {Appointment}
     */
    @Input() public appointment: Appointment;

    @ViewChild('autocomplete') searchbar: AutoCompleteComponent;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;


    /**
     * show loading
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * available languages
     *
     * @type array
     */
    public langs: any = [];

    /**
     * validate state
     *
     * @type {boolean}
     */
    public validate: boolean = false;

    /**
     * Validation errors
     *
     * @type {Object}
     */
    public validationErrors: any = {};

    /**
     * is autocomplete list opened?
     *
     * @type {boolean}
     */
    public autocompleteOpen: boolean = false;

     /**
     * array of participants to attach
     *
     * @type {Array<Participant>}
     */
     public participants: Array<Participant> = [];

    constructor(
        private keyboard: Keyboard,
        public router: Router,
        public plt: PlatformService,
        public modalController: ModalController,
        public conversationService: ConversationService,
        public appointmentService: AppointmentService,
        public participantService: ParticipantService,
        public overlayService: OverlayService,
        public translate: TranslateService,
        public userService: UserService,
    ) {

        // user language
        this.userLang = this.translate.currentLang;
        this.langs = this.translate.getLangs();

        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.userLang = event.lang;
        });

        if (this.plt.is('cordova')) {
            this.keyboard.setResizeMode(KeyboardResizeMode.Body);
        }
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {

        this.participantService.autocompleteOptions = {
            onlyContactable: true,
            hideNotVisibleMessage: true
        };

        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user: User) => {
            if (user.id) {
                this.user = new User(user);
                this.user.selected_participant.event = new Event(this.user.selected_participant.event, this.user);

                this.participantService.eventId = this.user.selected_participant.event.id;
            }
        });
    }

    /**
     * invite more participants to appointment
     *
     * @return void
     */
    public inviteParticipants(): void {
        this.validate = true;

        this.appointmentService.addParticipants(this.appointment.id, this.participants, this.user.selected_participant.event.id)
            .subscribe((appointment: Appointment) => {

                this.participants = [];
                this.appointment = new Appointment(appointment);
                this.appointment.setParticipantStatus(this.user.selected_participant_id)
                this.validate = false;

                this.overlayService.showSuccess(this.translate.instant('APPOINTMENT_REQUEST_SAVED'));

                // exit
                this.modalController.dismiss({ action: 'close', appointment: this.appointment });

            }, error => {

                this.validationErrors = {};
                const data = error.error;

                if (data.fields) {
                    for (const field in data.fields) {
                        if (data.fields.hasOwnProperty(field)) {
                            this.validationErrors[field] = data.fields[field].join(' ');
                        }
                    }
                }
                // TODO
                this.overlayService.showError(data.message);
            });
    }

    /**
     * check app resize (keyboard opening) for input focus
     */
    public autocompleteFocus() {
        this.plt.checkResize();
        // we will hide on small screens all to make autocomplete input bigger
        this.autocompleteOpen = true;
    }


    /**
     * check app resize (keyboard closing) for input blur
     */
    public autocompleteBlur(clear: boolean = false) {
        this.plt.checkResize(false);
        this.autocompleteOpen = false;
        // close autocomplete list when triggering manually cancel for autocomplete
        if (clear) {
            // NOTE[jg] do this only for manual, not on ionBlur for autocomplete component
            // that was causing big performance problems
            this.searchbar.clearValue(true);
        }
    }

    /**
     * Add new participant to appointment
     *
     * @param {Particiapnt} particiant event participant
     */
    public addParticipant(participant) {
        if (!this.participants.find((item) => { item.id == participant.id })) {
            this.participants.push(new Participant(participant))
        }

        // check resize of app and other items when bluring input
        this.autocompleteBlur();

        // reset value for autocomplet searc
        this.searchbar.clearValue();
        this.scrollBottom();
    }

    /**
     * Remove participant from appointment
     *
     * @param {Participant} participant event participant
     */
    public removeParticipant(participant) {
        this.participants = this.appointment.participants.filter((item) => {
            return item.id != participant.id
        });
    }

    public scrollBottom() {
        setTimeout(() => {
            document.getElementById('invitation-box').scrollIntoView({ block: 'end' });
        }, 300);
    }

    /**
     * close modal window
     *
     */
    public backClicked() {
        this.modalController.dismiss({ action: 'close' });
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.participantService.autocompleteOptions = null;

        if (this.plt.is('cordova')) {
            this.keyboard.setResizeMode(KeyboardResizeMode.Native);
        }
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}